/**
 * VirtVMsList — Máquinas virtuales (estándar de listados del CMDB).
 *
 * Antes: tabla propia con tema oscuro a mano, sin filtros de hipervisor, cluster
 * ni propiedad, y la ficha abriendo en edición.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura**. Los discos y las NICs salen en **diálogos** propios (el
 * dato se pide al detalle de la VM, que ya devolvía `disks` y `nics`).
 *
 * Botones propios:
 *  · **Ver sus discos** — diálogo con etiqueta, tamaño, tipo y datastore.
 *  · **Ver sus NICs** — diálogo con MAC, IP y el port group (y su VLAN) de cada
 *    una, con enlace al vSwitch.
 *  · **Ver el hipervisor** — a su ficha en el listado de hipervisores.
 *  · **Ver en el Diseñador de servicios** — al diagrama del elemento
 *    (`/services/designer?ci_type=virt_vm&ci_id=`, deep link añadido).
 *  · **Copiar** — alta precargada sin UUID, discos, NICs ni datos del agente.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ComputerDesktopIcon,
  ListBulletIcon,
  Squares2X2Icon,
  CircleStackIcon,
  SignalIcon,
  ServerStackIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import BulkDelegationAction from './common/BulkDelegationAction';
import BulkEditAction from './common/BulkEditAction';
import VirtVMForm from './VirtVMForm';
import { useListView } from '../views/ViewsContext';
import useNavOrder from '../hooks/useNavOrder';
import { tagList } from '../utils/tags';
import api from '../services/api';
import { getServerFormStyle } from '../utils/serverFormStyle';
import {
  hostingLabel,
  hostingProviderLabel,
  isCloudVm,
} from '../utils/hosting';

const POWER_META = {
  on:        { label: 'Encendida',   badge: 'list-badge--on' },
  off:       { label: 'Apagada',     badge: 'list-badge--off' },
  suspended: { label: 'Suspendida',  badge: 'list-badge--warn' },
  unknown:   { label: 'Desconocido', badge: 'list-badge' },
};

const AGENT_META = {
  online:     { label: 'Agente online',   badge: 'list-badge--on' },
  offline:    { label: 'Agente offline',  badge: 'list-badge--off' },
  'no-agent': { label: 'Sin agente',      badge: 'list-badge--warn' },
  unknown:    { label: 'Agente sin datos', badge: 'list-badge' },
};

const VM_TYPE_LABEL = {
  server:  'Servidor',
  desktop: 'Escritorio',
  network: 'Red (router/FW)',
  storage: 'Almacenamiento',
  other:   'Otro',
};

const DISK_TYPE_LABEL = {
  thick: 'Thick',
  thin:  'Thin',
  raw:   'Raw',
  qcow2: 'QCOW2',
};

const EMPTY_VM = { name: '', vm_type: 'server', power_state: 'off', os_name: '', cpu_cores: '', ram_gb: '' };

/* Valor sintético del filtro «Hipervisor» para las VMs delegadas: no tienen
   hipervisor visible porque la plataforma es del proveedor que las cede. */
const HOST_EXTERNAL = '__external__';

export default function VirtVMsList({ companyId, scope = 'all' }) {
  /* `scope="cloud"` es la entrada «Cloud / VPS» del menú: MISMO componente y
     mismo endpoint, pero solo las máquinas cuyo alojamiento no es un hipervisor
     propio visible (nubes públicas y VPS cedidos al cliente). Duplicar el
     listado habría dejado dos ficheros que arreglar por duplicado. */
  const isCloudScope = scope === 'cloud';
  const [estiloFicha] = useState(getServerFormStyle);
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [filterPower, setFilterPower] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterOs, setFilterOs]       = useState('');
  const [filterHyp, setFilterHyp]     = useState('');
  const [filterCluster, setFilterCluster] = useState('');
  const [filterEnv, setFilterEnv]     = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterOwn, setFilterOwn]     = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [groupMode, setGroupMode]     = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [cloneSource, setCloneSource] = useState(null);
  const [hlId, setHlId]               = useState(null);
  /* Vacía la selección masiva después de aplicar un cambio (las filas siguen
     siendo las mismas, pero el usuario ya no necesita tenerlas marcadas). */
  const [bulkResetKey, setBulkResetKey] = useState(0);

  /* Diálogos de discos y NICs (se piden al detalle de la VM) */
  const [detailVm, setDetailVm]     = useState(null);
  const [detailPane, setDetailPane] = useState(null);   // 'disks' | 'nics'
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /** Filtros que llegan por URL: los chips del resumen de virtualización y los
      botones de las listas vecinas enlazan aquí ya filtrado. */
  const qHypervisor = searchParams.get('hypervisorId');
  const qCluster    = searchParams.get('clusterId');
  const qPower      = searchParams.get('power');
  const qTemplate   = searchParams.get('template');

  useEffect(() => {
    if (qHypervisor) setFilterHyp(String(qHypervisor));
    if (qCluster) setFilterCluster(String(qCluster));
    if (qPower) setFilterPower(qPower);
    if (qTemplate) setFilterTemplate(qTemplate);
  }, [qHypervisor, qCluster, qPower, qTemplate]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getVMs(companyId);
      setItems(res.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando las máquinas virtuales');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find(v => v.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setCloneSource(null); setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`virt-vm-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, items, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (v) => { setCloneSource(null); setInitialMode('view');   setSelected(v); };
  const openEdit   = (v) => { setCloneSource(null); setInitialMode('edit');   setSelected(v); };
  const openCreate = ()  => { setCloneSource(null); setInitialMode('create'); setSelected({ ...EMPTY_VM }); };
  /** Copiar: lo único que no puede repetirse (UUID, discos, NICs y agente). */
  const openCopy   = (v) => {
    if (!v) return;
    setSelected(null);
    setCloneSource({
      ...v,
      id: undefined,
      uuid: '',
      fqdn: '',
      name: v.name ? `${v.name} (copia)` : '',
      is_template: false,
      disks: [],
      nics: [],
      disk_count: 0,
      nic_count: 0,
      agent_id: null,
      agent_status: 'unknown',
      agent_version: null,
      last_seen_at: null,
      guest_agent: null,
    });
    setInitialMode('create');
  };
  const closeForm = () => { setSelected(null); setCloneSource(null); load(); };

  const handleDelete = async (v) => {
    try {
      await api.deleteVM(v.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando la máquina virtual');
    }
  };

  /* ── discos y NICs ──────────────────────────────────────────────────────── */
  const openDetail = async (vm, pane) => {
    setDetailVm(vm);
    setDetailPane(pane);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.getVM(vm.id, companyId);
      setDetail(res.data || null);
    } catch (e) {
      setError(e.message || 'Error cargando el detalle de la máquina virtual');
      setDetailVm(null);
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setDetailVm(null); setDetailPane(null); setDetail(null); };

  /* ── propiedad (comparar en texto: companyId llega a veces como string) ─── */
  const isDelegated = (vm) =>
    String(vm.company_id) !== String(companyId)
    && String(vm.delegated_company_id) === String(companyId);
  const isOwner = (vm) => String(vm.company_id) === String(companyId);
  /* Alojamiento para los avisos de discos y NICs: solo cuando el dato es de
     otra empresa (delegada) o de un proveedor externo. En una VM propia con
     hipervisor, que falte el datastore es un hueco normal y se pinta «—». */
  const hostingHint = (vm) => {
    if (vm && (vm.hosting_hidden || vm.hosting_is_external)) {
      return hostingLabel(vm) || tr('virt.external_hosting', 'Infraestructura externa');
    }
    return '—';
  };

  /* ── opciones de filtro (derivadas de los datos) ─────────────────────────── */
  const optionsOf = (field, labeler) => {
    const seen = new Map();
    items.forEach((v) => {
      const raw = v[field];
      if (raw === null || raw === undefined || raw === '') return;
      const label = labeler ? labeler(v, raw) : String(raw);
      if (label) seen.set(String(raw), label);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], 'es'))
      .map(([value, label]) => ({ value, label }));
  };

  const osOptions = useMemo(() => optionsOf('os_name'), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Hipervisores que ya tienen VMs + «Sin hipervisor» si hay alguna suelta: los
      chips del resumen enlazan con `?hypervisorId=none` y así el select lo pinta. */
  const hypOptions = useMemo(() => {
    const opts = optionsOf('hypervisor_id', (v) => v.hypervisor_name);
    if (items.some(v => !v.hypervisor_id && !isDelegated(v))) {
      opts.push({ value: 'none', label: tr('virt.no_hypervisor', 'Sin hipervisor') });
    }
    if (items.some(isDelegated)) {
      opts.push({ value: HOST_EXTERNAL, label: tr('virt.external_hosting', 'Infraestructura externa') });
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, t]);

  const clusterOptions = useMemo(
    () => optionsOf('cluster_id', (v) => v.cluster_name),
    [items], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const envOptions = useMemo(() => optionsOf('environment'), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  /* Proveedor de plataforma: quién proporciona el alojamiento. */
  const providerOptions = useMemo(
    () => optionsOf('hosting_provider', (v) => v.hosting_provider_label || hostingProviderLabel(v.hosting_provider)),
    [items], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((vm) => {
      /* Cloud / VPS: solo lo que NO corre en un hipervisor propio visible.
         Las que no tienen ni hipervisor ni proveedor no son cloud: son un dato
         que falta, y se quedan fuera. */
      if (isCloudScope && !isCloudVm(vm)) return false;
      if (filterProvider && String(vm.hosting_provider || '') !== String(filterProvider)) return false;
      if (filterPower && (vm.power_state || 'unknown') !== filterPower) return false;
      if (filterAgent && (vm.agent_status || 'unknown') !== filterAgent) return false;
      if (filterOs && vm.os_name !== filterOs) return false;
      if (filterHyp === 'none' && (vm.hypervisor_id || isDelegated(vm))) return false;
      if (filterHyp === HOST_EXTERNAL && !isDelegated(vm)) return false;
      if (filterHyp && filterHyp !== 'none' && filterHyp !== HOST_EXTERNAL && String(vm.hypervisor_id || '') !== String(filterHyp)) return false;
      if (filterCluster && String(vm.cluster_id || '') !== String(filterCluster)) return false;
      if (filterEnv && vm.environment !== filterEnv) return false;
      if (filterTemplate === 'yes' && !vm.is_template) return false;
      if (filterTemplate === 'no' && vm.is_template) return false;
      if (filterOwn === 'mine' && !isOwner(vm)) return false;
      if (filterOwn === 'delegated-out' && !(isOwner(vm) && vm.delegated_company_id)) return false;
      if (filterOwn === 'delegated-in' && !isDelegated(vm)) return false;
      if (filterDomain === 'yes' && !vm.part_of_domain) return false;
      if (filterDomain === 'no' && vm.part_of_domain) return false;
      if (!needle) return true;
      return [
        vm.name, vm.fqdn, vm.uuid, vm.os_name, vm.os_version, vm.domain_name,
        vm.hypervisor_name, vm.cluster_name, vm.environment,
        tagList(vm.tags).join(' '),
      ].filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, companyId, filterPower, filterAgent, filterOs, filterHyp,
    filterCluster, filterEnv, filterTemplate, filterOwn, filterDomain,
    filterProvider, isCloudScope]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navVms = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('virt.name', 'Nombre'),
        sortable: true,
        render: (vm) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {vm.is_template && <span className="list-badge">TPL</span>}
              <span style={{ fontWeight: 600 }}>{vm.name}</span>
            </span>
            <span className="list-hint">{VM_TYPE_LABEL[vm.vm_type] || vm.vm_type || ''}</span>
          </span>
        ),
      },
      {
        key: 'os',
        label: tr('virt.os', 'Sistema operativo'),
        sortable: true,
        sortValue: (vm) => `${vm.os_name || ''} ${vm.os_version || ''}`.trim(),
        render: (vm) => (vm.os_name
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{vm.os_name}</span>
              {vm.os_version && <span className="list-hint">{vm.os_version}</span>}
            </span>
          )
          : dash),
      },
      {
        key: 'host',
        label: tr('virt.hosting', 'Alojamiento'),
        sortable: true,
        sortValue: (vm) => vm.hypervisor_name || vm.cluster_name || vm.hosting_provider_label || '',
        render: (vm) => (vm.hypervisor_name || vm.cluster_name
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{vm.hypervisor_name || dash}</span>
              {vm.cluster_name && <span className="list-hint">{vm.cluster_name}</span>}
            </span>
          )
          : (hostingLabel(vm)
            ? (
              <span
                className="list-hint"
                title="Plataforma que proporciona la máquina: no forma parte del inventario de tu empresa"
              >
                {hostingLabel(vm)}
              </span>
            )
            : dash)),
      },
      {
        key: 'compute',
        label: tr('virt.compute', 'vCPU / RAM'),
        sortable: true,
        sortValue: (vm) => vm.cpu_cores ?? 0,
        render: (vm) => (vm.cpu_cores || vm.ram_gb
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{vm.cpu_cores ? `${vm.cpu_cores} vCPU` : ''}</span>
              <span className="list-hint">{vm.ram_gb ? `${vm.ram_gb} GB` : ''}</span>
            </span>
          )
          : dash),
      },
      {
        key: 'disk_total_gb',
        label: tr('virt.disk', 'Disco'),
        sortable: true,
        sortValue: (vm) => vm.disk_total_gb ?? 0,
        render: (vm) => (vm.disk_total_gb
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{vm.disk_total_gb} GB</span>
              <span className="list-hint">{vm.disk_count || 0} disco(s)</span>
            </span>
          )
          : dash),
      },
      {
        key: 'power_state',
        label: tr('virt.power', 'Estado'),
        sortable: true,
        sortValue: (vm) => vm.power_state || 'unknown',
        render: (vm) => {
          const meta = POWER_META[vm.power_state] || POWER_META.unknown;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      {
        key: 'agent_status',
        label: tr('virt.agent', 'Agente'),
        sortable: true,
        sortValue: (vm) => vm.agent_status || 'unknown',
        render: (vm) => {
          const meta = AGENT_META[vm.agent_status || 'unknown'] || AGENT_META.unknown;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
              <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              {vm.last_seen_at && <span className="list-hint">{new Date(vm.last_seen_at).toLocaleString()}</span>}
            </span>
          );
        },
      },
      {
        key: 'ownership',
        label: tr('virt.ownership', 'Propiedad'),
        sortValue: (vm) => (isDelegated(vm) ? 'delegada' : 'propietario'),
        render: (vm) => {
          const delegated = isDelegated(vm);
          const owner = isOwner(vm);
          if (delegated) {
            return (
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                <span className="list-badge">{tr('virt.delegated', 'Delegada')}</span>
                {vm.company_name && <span className="list-hint">{tr('virt.from', 'de')} {vm.company_name}</span>}
              </span>
            );
          }
          if (owner && vm.delegated_company_id) {
            return (
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                <span className="list-badge list-badge--on">{tr('virt.owner_shared', 'Propietario (cedida)')}</span>
                <span className="list-hint">→ {vm.delegated_company_name || `#${vm.delegated_company_id}`}</span>
              </span>
            );
          }
          return <span className="list-badge list-badge--on">{tr('virt.owner', 'Propietaria')}</span>;
        },
      },
      {
        key: 'environment',
        label: tr('virt.environment', 'Entorno'),
        sortable: true,
        render: (vm) => (vm.environment ? <span className="list-badge">{vm.environment}</span> : dash),
      },
      {
        key: 'domain',
        label: tr('virt.ad', 'Dominio AD'),
        sortValue: (vm) => (vm.part_of_domain ? 1 : 0),
        render: (vm) => {
          if (!vm.part_of_domain && !vm.domain_name) return dash;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span className="list-badge list-badge--on">{tr('virt.ad_yes', 'En dominio')}</span>
              {vm.domain_name && <span className="list-hint list-code">{vm.domain_name}</span>}
              {vm.ad_dc && <span className="list-hint">{tr('virt.ad_dc', 'Controlador de dominio')}</span>}
            </span>
          );
        },
      },
      {
        key: 'tags',
        label: tr('virt.tags', 'Etiquetas'),
        render: (vm) => {
          const tags = tagList(vm.tags);
          if (!tags.length) return dash;
          return (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {tags.slice(0, 3).map(tag => <span key={tag} className="list-badge">{tag}</span>)}
              {tags.length > 3 && <span className="list-hint">+{tags.length - 3}</span>}
            </span>
          );
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (vm) => vm.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr]);

  const defaultVisible = ['name', 'os', 'host', 'compute', 'power_state', 'agent_status', 'ownership', 'environment'];

  const filters = [
    {
      key: 'power', label: tr('virt.power', 'Estado'), allLabel: tr('virt.power_all', 'Cualquier estado'),
      value: filterPower, onChange: setFilterPower,
      options: Object.entries(POWER_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'agent', label: tr('virt.agent', 'Agente'), allLabel: tr('virt.agent_all', 'Cualquier agente'),
      value: filterAgent, onChange: setFilterAgent,
      options: Object.entries(AGENT_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'os', label: tr('virt.os', 'Sistema operativo'), allLabel: tr('virt.os_all', 'Cualquier sistema'),
      value: filterOs, onChange: setFilterOs, options: osOptions,
    },
    {
      key: 'hypervisor', label: tr('virt.hypervisor', 'Hipervisor'), allLabel: tr('virt.hyp_all', 'Cualquier hipervisor'),
      value: filterHyp, onChange: setFilterHyp, options: hypOptions,
    },
    {
      key: 'provider', label: tr('virt.hosting_provider', 'Proveedor'), allLabel: tr('virt.provider_all', 'Cualquier proveedor'),
      value: filterProvider, onChange: setFilterProvider, options: providerOptions,
    },
    {
      key: 'cluster', label: tr('virt.cluster', 'Cluster'), allLabel: tr('virt.cluster_all', 'Cualquier cluster'),
      value: filterCluster, onChange: setFilterCluster, options: clusterOptions,
    },
    {
      key: 'environment', label: tr('virt.environment', 'Entorno'), allLabel: tr('virt.env_all', 'Cualquier entorno'),
      value: filterEnv, onChange: setFilterEnv, options: envOptions,
    },
    {
      key: 'template', label: tr('virt.template', 'Plantillas'), allLabel: tr('virt.template_all', 'Plantillas: todas'),
      value: filterTemplate, onChange: setFilterTemplate,
      options: [
        { value: 'yes', label: tr('virt.template_yes', 'Solo plantillas') },
        { value: 'no', label: tr('virt.template_no', 'Sin plantillas') },
      ],
    },
    {
      key: 'ownership', label: tr('virt.ownership', 'Propiedad'), allLabel: tr('virt.own_all', 'Cualquier propiedad'),
      value: filterOwn, onChange: setFilterOwn,
      options: [
        { value: 'mine', label: tr('virt.own_mine', 'Mías') },
        { value: 'delegated-out', label: tr('virt.own_out', 'Cedidas a otra empresa') },
        { value: 'delegated-in', label: tr('virt.own_in', 'Delegadas a mí') },
      ],
    },
    {
      key: 'domain', label: tr('virt.ad', 'Dominio AD'), allLabel: tr('virt.ad_all', 'Dominio: cualquiera'),
      value: filterDomain, onChange: setFilterDomain,
      options: [
        { value: 'yes', label: tr('virt.ad_yes', 'En dominio') },
        { value: 'no', label: tr('virt.ad_no', 'Fuera de dominio') },
      ],
    },
    {
      key: 'group', label: tr('virt.group', 'Agrupar por'), allLabel: tr('virt.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'hypervisor', label: tr('virt.hypervisor', 'Hipervisor') },
        { value: 'provider', label: tr('virt.hosting_provider', 'Proveedor') },
        { value: 'power', label: tr('virt.power', 'Estado de encendido') },
        { value: 'os', label: tr('virt.os', 'Sistema operativo') },
      ],
    },
  ];

  const subtitle = isCloudScope
    ? `${filtered.length} ${filtered.length === 1
      ? tr('virt.cloud_single', 'máquina en cloud o cedida')
      : tr('virt.cloud_plural', 'máquinas en cloud o cedidas')}`
    : `${items.length} ${tr('virt.vm_plural', 'máquinas virtuales')}`
      + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const groupBy = useMemo(() => {
    if (groupMode === 'hypervisor') {
      return (vm) => hostingLabel(vm) || tr('virt.no_hypervisor', 'Sin hipervisor');
    }
    if (groupMode === 'provider') {
      return (vm) => hostingProviderLabel(vm.hosting_provider)
        || tr('virt.no_provider', 'Sin proveedor');
    }
    if (groupMode === 'power') {
      return (vm) => (POWER_META[vm.power_state] || POWER_META.unknown).label;
    }
    if (groupMode === 'os') {
      return (vm) => vm.os_name || tr('virt.no_os', 'Sin sistema operativo');
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, items]);

  const exportRows = useMemo(() => filtered.map(vm => ({
    'Nombre': vm.name || '',
    'Plantilla': vm.is_template ? 'Sí' : 'No',
    'SO': vm.os_name || '',
    'Versión SO': vm.os_version || '',
    'FQDN': vm.fqdn || '',
    'Dominio': vm.domain_name || '',
    'Controlador de dominio': vm.ad_dc ? 'Sí' : '',
    'Hipervisor': vm.hypervisor_name || '',
    'Clúster': vm.cluster_name || '',
    'Proveedor': vm.hosting_provider_label || hostingProviderLabel(vm.hosting_provider) || '',
    'Región': vm.hosting_region || '',
    'vCPU': vm.cpu_cores ?? '',
    'RAM (GB)': vm.ram_gb ?? '',
    'Disco total (GB)': vm.disk_total_gb ?? '',
    'Nº discos': vm.disk_count ?? '',
    'Nº NICs': vm.nic_count ?? '',
    'Entorno': vm.environment || '',
    'Estado': (POWER_META[vm.power_state] || POWER_META.unknown).label,
    'Agente': (AGENT_META[vm.agent_status || 'unknown'] || AGENT_META.unknown).label,
    'Última vez visto': vm.last_seen_at ? new Date(vm.last_seen_at).toLocaleString() : '',
    'Versión del agente': vm.agent_version || '',
    'Propiedad': isDelegated(vm) ? 'Delegada' : 'Propietaria',
    'Empresa propietaria': vm.company_name || '',
    'Empresa delegada': vm.delegated_company_name || '',
    'Etiquetas': tagList(vm.tags).join(', '),
    'UUID': vm.uuid || '',
  })), [filtered, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Tarjetas: lo esencial de la VM, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((vm) => {
          const power = POWER_META[vm.power_state] || POWER_META.unknown;
          const agent = AGENT_META[vm.agent_status || 'unknown'] || AGENT_META.unknown;
          return (
            <div
              key={vm.id}
              id={`virt-vm-card-${vm.id}`}
              className={`list-card${vm.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(vm)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(vm); }}
              title={`${tr('action.view', 'Ver')} ${vm.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">
                    {vm.is_template ? 'TPL · ' : ''}{vm.name}
                  </div>
                  {vm.fqdn && <span className="list-code">{vm.fqdn}</span>}
                </div>
                <span className={`list-badge ${power.badge}`}>{power.label}</span>
              </div>

              <div className="list-card-meta">
                {vm.os_name && <span>{vm.os_name}{vm.os_version ? ` ${vm.os_version}` : ''}</span>}
                {hostingLabel(vm) && <span>{hostingLabel(vm)}</span>}
                {vm.cpu_cores ? <span>{vm.cpu_cores} vCPU</span> : null}
                {vm.ram_gb ? <span>{vm.ram_gb} GB RAM</span> : null}
                {vm.disk_total_gb ? <span>{vm.disk_total_gb} GB disco</span> : null}
                <span className={`list-badge ${agent.badge}`}>{agent.label}</span>
              </div>

              <div className="list-card-actions">{rowActions(vm)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const VM_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada VM'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView(isCloudScope ? 'virt_cloud' : 'virt_vms', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('virt.select_company', 'Selecciona una empresa para ver la virtualización.')}
        </div>
      </div>
    );
  }

  const clearQueryFilter = (which) => {
    if (which === 'hypervisor') setFilterHyp('');
    if (which === 'cluster') setFilterCluster('');
    if (which === 'power') setFilterPower('');
    if (which === 'template') setFilterTemplate('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (which === 'hypervisor') next.delete('hypervisorId');
      if (which === 'cluster') next.delete('clusterId');
      if (which === 'power') next.delete('power');
      if (which === 'template') next.delete('template');
      return next;
    }, { replace: true });
  };

  return (
    <>
      {(selected !== null || cloneSource) && (
        <VirtVMForm
          vm={cloneSource || (selected && selected.id ? selected : null)}
          companyId={companyId}
          initialMode={cloneSource ? 'create' : initialMode}
          variant={estiloFicha}
          navItems={navVms}
          navIndex={!cloneSource && selected && selected.id ? navVms.findIndex(v => String(v.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      {/* Edición masiva: solo las VMs PROPIAS se pueden seleccionar. Una VM
          delegada a esta empresa es de otra: se ve, pero no se toca. */}
      <div style={{ display: (selected !== null || cloneSource) ? 'none' : undefined }}>
        <ListPage
          icon={ComputerDesktopIcon}
          title={isCloudScope
            ? tr('virt.cloud_title', 'VPS y nubes públicas')
            : tr('virt.vm_title', 'Máquinas virtuales')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('virt.new_vm', 'Nueva VM')}
          createTitle={tr('virt.new_vm_hint', 'Inventario de máquinas virtuales de la empresa')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('virt.vm_search', 'Buscar por nombre, FQDN, SO, hipervisor, etiqueta…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase={isCloudScope ? 'cloud-vps' : 'maquinas-virtuales'}
          exportSheetName={isCloudScope ? 'Cloud y VPS' : 'Máquinas virtuales'}

          columns={columns}
          columnStorageKey={isCloudScope ? 'cmdb_cols_virt_cloud' : 'cmdb_cols_virt_vms'}
          companyId={companyId}
          importEntity="virt-vms"
          onImported={() => load()}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(vm) => vm.id}
          rowId={(vm) => `virt-vm-row-${vm.id}`}
          rowClassName={(vm) => (vm.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(vm) => `${tr('action.view', 'Ver')} ${vm.name}`}
          groupBy={groupBy}

          selectable={(vm) => isOwner(vm)}
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkDelegationAction
                companyId={companyId}
                entity="virt_vms"
                selectedRows={selectedRows}
                onApplied={() => { load(); setBulkResetKey((k) => k + 1); }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="virt_vms"
                selectedRows={selectedRows}
                onApplied={() => { load(); setBulkResetKey((k) => k + 1); }}
              />
            </>
          )}

          headerActions={(
            <>
              {qHypervisor && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => clearQueryFilter('hypervisor')}
                  title={tr('virt.clear_filter', 'Quitar este filtro')}
                >
                  × {tr('virt.filtered_by_hypervisor', 'Solo este hipervisor')}
                </button>
              )}
              {qCluster && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => clearQueryFilter('cluster')}
                  title={tr('virt.clear_filter', 'Quitar este filtro')}
                >
                  × {tr('virt.filtered_by_cluster', 'Solo este cluster')}
                </button>
              )}
              {qPower && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => clearQueryFilter('power')}
                  title={tr('virt.clear_filter', 'Quitar este filtro')}
                >
                  × {(POWER_META[qPower] || POWER_META.unknown).label}
                </button>
              )}
              {qTemplate && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => clearQueryFilter('template')}
                  title={tr('virt.clear_filter', 'Quitar este filtro')}
                >
                  × {tr('virt.template_yes', 'Solo plantillas')}
                </button>
              )}
            </>
          )}

          views={VM_VIEWS}
          listKey="virt_vms"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando máquinas virtuales…')}
          error={error}

          empty={{
            icon: '💻',
            title: items.length === 0
              ? tr('virt.vm_empty', 'No hay máquinas virtuales')
              : tr('msg.no_results', 'Ninguna máquina virtual coincide con los filtros'),
            subtitle: items.length === 0
              ? tr('virt.vm_empty_hint', 'Registra las VMs para llevar el inventario completo de la infraestructura virtual.')
              : undefined,
            action: items.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('virt.new_vm', 'Nueva VM')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (vm) => (
              <>
                {/* Ver sus discos */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(vm.disk_count > 0)}
                  onClick={() => openDetail(vm, 'disks')}
                  title={tr('virt.view_disks', 'Ver sus discos')}
                >
                  <CircleStackIcon />
                </button>
                {/* Ver sus NICs */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(vm.nic_count > 0)}
                  onClick={() => openDetail(vm, 'nics')}
                  title={tr('virt.view_nics', 'Ver sus NICs')}
                >
                  <SignalIcon />
                </button>
                {/* Ver el hipervisor */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!vm.hypervisor_id}
                  onClick={() => navigate(`/virt/hypervisors?destacar=${vm.hypervisor_id}&abrir=1`)}
                  title={vm.hypervisor_name
                    ? `${tr('virt.view_hypervisor', 'Ver el hipervisor')}: ${vm.hypervisor_name}`
                    : (hostingLabel(vm)
                      || tr('virt.no_hypervisor', 'Sin hipervisor'))}
                >
                  <ServerStackIcon />
                </button>
                {/* Ver en el Diseñador de servicios (diagrama del elemento) */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(
                    `/services/designer?ci_type=virt_vm&ci_id=${vm.id}&ci_name=${encodeURIComponent(vm.name || '')}`,
                  )}
                  title={tr('virt.view_in_designer', 'Ver en el Diseñador de servicios')}
                >
                  <ShareIcon />
                </button>
              </>
            ),
            onCopy: openCopy,
            onEdit: openEdit,
            canEdit: (vm) => isOwner(vm),
            onDelete: handleDelete,
            canDelete: (vm) => isOwner(vm),
            deleteTitle: tr('virt.vm_delete_title', 'Eliminar máquina virtual'),
            deleteMessage: (vm) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{vm.name}</strong>?{' '}
                {tr('virt.vm_delete_used', 'Se eliminarán también sus discos y sus NICs.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo: discos / NICs ══ */}
      {detailVm && (
        <Dialog
          title={`${detailPane === 'nics' ? tr('virt.nics_of', 'NICs de') : tr('virt.disks_of', 'Discos de')} ${detailVm.name}`}
          icon={detailPane === 'nics'
            ? <SignalIcon style={{ width: 18, height: 18 }} />
            : <CircleStackIcon style={{ width: 18, height: 18 }} />}
          maxWidth={760}
          onClose={closeDetail}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={closeDetail}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {detailLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}

          {!detailLoading && detail && detailPane === 'disks' && (
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{tr('virt.disk_label', 'Disco')}</th>
                  <th>{tr('virt.size', 'Tamaño')}</th>
                  <th>{tr('virt.disk_type', 'Tipo')}</th>
                  <th>Datastore</th>
                  <th>{tr('virt.path', 'Ruta')}</th>
                </tr>
              </thead>
              <tbody>
                {(detail.disks || []).map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 600 }}>{d.disk_label || `#${d.id}`}</td>
                    <td>{d.size_gb ? `${d.size_gb} GB` : <span className="list-hint">—</span>}</td>
                    <td><span className="list-badge">{DISK_TYPE_LABEL[d.disk_type] || d.disk_type || '—'}</span></td>
                    <td>{d.datastore_name || <span className="list-hint">{hostingHint(detailVm)}</span>}</td>
                    <td>{d.path ? <span className="list-code">{d.path}</span> : <span className="list-hint">{hostingHint(detailVm)}</span>}</td>
                  </tr>
                ))}
                {(detail.disks || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="list-hint" style={{ textAlign: 'center' }}>
                      {tr('virt.no_disks', 'Esta máquina no tiene discos declarados.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {!detailLoading && detail && detailPane === 'nics' && (
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>NIC</th>
                  <th>{tr('virt.adapter', 'Adaptador')}</th>
                  <th>MAC</th>
                  <th>IP</th>
                  <th>{tr('virt.pg_short', 'Port group')}</th>
                  <th>VLAN</th>
                </tr>
              </thead>
              <tbody>
                {(detail.nics || []).map((n) => (
                  <tr key={n.id}>
                    <td style={{ fontWeight: 600 }}>{n.nic_label || `#${n.id}`}</td>
                    <td>{n.adapter_type ? <span className="list-badge">{n.adapter_type}</span> : <span className="list-hint">—</span>}</td>
                    <td>{n.mac_address ? <span className="list-code">{n.mac_address}</span> : <span className="list-hint">—</span>}</td>
                    <td>{n.ip_address ? <span className="list-code">{n.ip_address}</span> : <span className="list-hint">—</span>}</td>
                    <td>{n.port_group_name || <span className="list-hint">{hostingHint(detailVm)}</span>}</td>
                    <td>{n.vlan_id ? <span className="list-code">{n.vlan_id}</span> : <span className="list-hint">{hostingHint(detailVm)}</span>}</td>
                  </tr>
                ))}
                {(detail.nics || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="list-hint" style={{ textAlign: 'center' }}>
                      {tr('virt.no_nics', 'Esta máquina no tiene NICs declaradas.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </Dialog>
      )}
    </>
  );
}
